// Priority SEO page: How to choose a plastic surgeon in Vietnam.
const choosingSurgeon = {
  slug: "how-to-choose-a-plastic-surgeon-in-vietnam",
  metaTitle: "How to Choose a Plastic Surgeon in Vietnam",
  metaDescription:
    "A practical guide to checking licences, procedure experience, operating facilities, anaesthesia, results, follow-up and other surgeon-selection factors.",
  eyebrow: "Choosing a Surgeon",
  h1: "How to Choose a Plastic Surgeon in Vietnam",
  intro:
    "Choosing the right surgeon is the most important decision you will make. This practical guide explains what to verify, what to ask, and the warning signs that should give you pause.",
  summary:
    "Confirm the surgeon's identity and licence, check experience with your specific procedure, understand where surgery will take place and who provides anaesthesia, and expect a calm consultation without pressure. Avoid anyone who guarantees results or rushes your decision.",
  published: "December 2025",
  updated: "December 2025",
  breadcrumb: [{ name: "Choosing a Surgeon", to: "/how-to-choose-a-plastic-surgeon-in-vietnam" }],
  sections: [
    {
      id: "identity",
      h2: "Confirm the surgeon's identity and licensing",
      paragraphs: [
        "Start by confirming who will actually perform your surgery, not only who you speak to during marketing or coordination. Ask for the surgeon's full name and confirm they are licensed to perform your procedure.",
        "Ask how you can verify this information independently. A confident, reputable surgeon will not object to reasonable verification questions.",
      ],
    },
    {
      id: "experience",
      h2: "Check procedure-specific experience",
      paragraphs: [
        "General surgical experience is not the same as experience with your exact procedure. A surgeon who performs your procedure regularly is usually preferable to one who performs it occasionally.",
      ],
      bullets: [
        "How often do you perform this specific procedure?",
        "What techniques do you use, and why might they suit my case?",
        "Can you show relevant, consented before-and-after cases similar to mine?",
      ],
    },
    {
      id: "facility",
      h2: "Ask where surgery will take place",
      paragraphs: [
        "The operating location matters. Ask whether surgery is performed in a licensed hospital or an appropriate facility, and what monitoring and emergency support are available.",
      ],
    },
    {
      id: "anaesthesia",
      h2: "Understand who provides anaesthesia",
      paragraphs: [
        "For many procedures, a qualified anaesthetist and appropriate monitoring are essential to safety. Ask who administers your anaesthesia and how you are monitored during and after surgery.",
      ],
    },
    {
      id: "results",
      h2: "Request realistic before-and-after cases",
      paragraphs: [
        "Ask to see consented before-and-after examples of cases similar to yours. Be cautious of images that look heavily edited or unrealistic. A trustworthy surgeon discusses realistic outcomes and limitations, not perfection.",
      ],
    },
    {
      id: "complications",
      h2: "Ask about complications, revisions and follow-up",
      subsections: [
        { h3: "Complications and revisions", paragraphs: ["Ask how complications are managed and what happens if a revision is needed, including who is responsible for costs."] },
        { h3: "Follow-up after you travel home", paragraphs: ["Understand who provides follow-up once you return home and how you stay in contact with your care team."] },
        { h3: "Communication and language support", paragraphs: ["Confirm how you will communicate before, during and after surgery, and whether language support is available."] },
      ],
    },
    {
      id: "pressure",
      h2: "Avoid pressure and limited-time offers",
      paragraphs: [
        "Responsible care gives you time to decide. Be wary of pressure to book quickly, limited-time discounts, or claims that a result is guaranteed. A good surgeon supports an unhurried, informed decision.",
      ],
    },
    {
      id: "red-flags",
      h2: "Red flags",
      bullets: [
        "Guarantees of a specific result or \"perfect\" outcome",
        "Refusal to confirm licensing or where surgery takes place",
        "Pressure to pay a deposit immediately",
        "No clear plan for complications or follow-up",
        "Prices that seem too good to be true with no explanation",
        "Marketing that focuses on discounts rather than safety",
      ],
    },
    {
      id: "checklist",
      h2: "Consultation question checklist",
      paragraphs: ["Take these questions to any consultation. A downloadable checklist will be available here."],
      bullets: [
        "Are you licensed for this procedure and can I verify it?",
        "How often do you perform it, and what technique suits me?",
        "Where will surgery take place, and who provides anaesthesia?",
        "What are my realistic outcomes, risks and limitations?",
        "How are complications, revisions and follow-up handled?",
      ],
      callout: "A downloadable consultation checklist is being prepared and will be added to this page.",
    },
  ],
  faqs: [
    { q: "How do I check a plastic surgeon's licence in Vietnam?", a: "Ask the surgeon directly for their credentials and how you can verify them, and confirm where surgery will take place. We can help organise the information needed for review." },
    { q: "Should I choose a surgeon based on price?", a: "Price should not be the main factor. Procedure-specific experience, an appropriate facility, safe anaesthesia and clear aftercare are more important." },
    { q: "What is a major red flag?", a: "Any guarantee of results, pressure to book quickly, or refusal to confirm licensing and the operating location. Responsible care supports an unhurried, informed decision." },
    { q: "Can Plastic Surgery Vietnam recommend the best surgeon?", a: "We do not publish rankings or \"best surgeon\" lists. We help identify appropriately qualified professionals and organise direct consultations so you can decide." },
  ],
  sources: [
    { label: "International Society of Aesthetic Plastic Surgery (ISAPS) — Find a surgeon guidance", url: "https://www.isaps.org/" },
    { label: "NHS — Choosing who will do your cosmetic procedure", url: "https://www.nhs.uk/conditions/cosmetic-procedures/" },
  ],
  relatedTitle: "Related guides & procedures",
  related: [
    { to: "/plastic-surgery-safety-vietnam", title: "Is plastic surgery in Vietnam safe?", desc: "How safety is determined" },
    { to: "/plastic-surgery-cost-vietnam", title: "Plastic surgery costs in Vietnam", desc: "What a quotation includes" },
    { to: "/rhinoplasty-vietnam", title: "Rhinoplasty in Vietnam", desc: "Explore nose surgery options" },
    { to: "/patient-journey", title: "The patient journey", desc: "Every step before you travel" },
  ],
};

export default choosingSurgeon;
